import axios, { AxiosRequestConfig, AxiosInstance } from 'axios';
import { getUserId } from '@/infrastructure/auth';
import { onReqFulfilled } from '@/infrastructure/axios/interceptors/request';
import { onResFulfilled, onResRejected } from '@/infrastructure/axios/interceptors/response';

const defaultConfig: AxiosRequestConfig = {
  timeout: 100000,
  baseURL: process.env.PROXY === 'true' ? '' : `${location.protocol}//${location.host}`,
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
  headers: {
    common: {
      ['X-Requested-With']: 'XMLHttpRequest',
      ['Content-Type']: 'application/json',
    },
  },
};

interface AxiosInterceptor {
  onFulfilled: (value: any) => any | Promise<any>,
  onRejected?: (error: any) => any
}

interface AbstractHttpClientInterceptor {
  request?: AxiosInterceptor;
  response?: AxiosInterceptor;
}

export interface InitParams {
  axiosConfig?: AxiosRequestConfig;
  serviceName?: string;
  interceptors?: AbstractHttpClientInterceptor;
  merged?: boolean;
}

const TRPC_REQUEST_PREFIX = '/v5/trpc';

export default class AbstractHttpClient {
  // @ts-ignore
  public client: AxiosInstance;

  private useTrpc = false;

  private serviceName?: string;

  constructor(config: InitParams) {
    const { merged = true, axiosConfig, serviceName, interceptors } = config;
    const finalAxiosConfig = this.mergeConfig(axiosConfig as AxiosRequestConfig, merged);
    this.serviceName = serviceName;
    this.useTrpc = !!serviceName;
    this.initAxiosClient(finalAxiosConfig, interceptors);
  }

  public async trpcGet<T = any>(funcName: string, config?: AxiosRequestConfig): Promise<T> {
    const path = `/${this.serviceName}/${funcName}`;
    return this.client.get(path, {
      headers: {
        'Content-Type': 'application/json',
        'Server-Type': 'trpc',
        user: getUserId(),
      },
      ...config,
    });
  }

  public async trpcPost<T = any>(funcName: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const path = `/${this.serviceName}/${funcName}`;
    return this.client.post(path, data, {
      headers: {
        'Content-Type': 'application/json',
        'Server-Type': 'trpc',
        user: getUserId(),
      },
      ...config,
    });
  }

  private mergeConfig(axiosConfig: AxiosRequestConfig, merged: boolean) {
    let finalAxiosConfig = defaultConfig;

    if (axiosConfig) {
      finalAxiosConfig = merged ? { ...defaultConfig, ...axiosConfig } : axiosConfig;
    }
    return finalAxiosConfig;
  }

  private initAxiosClient(config: AxiosRequestConfig, interceptors?: AbstractHttpClientInterceptor) {
    const { baseURL } = config;
    this.client = axios.create({
      ...config,
      baseURL: this.useTrpc ? TRPC_REQUEST_PREFIX : baseURL,
    });

    if (!interceptors) {
      this.client.interceptors.request.use(onReqFulfilled);
      this.client.interceptors.response.use(onResFulfilled, onResRejected);
      return;
    }

    this.client.interceptors.request.use(interceptors?.request?.onFulfilled, interceptors?.request?.onRejected);
    this.client.interceptors.response.use(interceptors?.response?.onFulfilled, interceptors?.response?.onRejected);
  };
}
